import React, { useState, useMemo, useEffect } from 'react';
import MaterialTable from 'material-table';
import { IconButton, Tooltip, withStyles, Typography } from '@material-ui/core';
import { Refresh } from '@material-ui/icons';
import * as actions from './store/printQueueActions';
import * as action from '../../np-core/Auth/store/authActions';
import { BackButton, getProductData, formatDate, localization, getDimensionsOfPagination } from '../../sc-utilities';
import DataRange from '../../sc-utilities/DateRange';
import moment from 'moment'
import * as Yup from "yup";
import { connect } from 'react-redux';
import PrintQueue from './PrintQueueReprint'
import { CsvBuilder } from 'filefy';
import momentTimezone from 'moment-timezone';

const styles = theme => ({
    "@global": {
        "[class*='buttonsCustomer']": {
            margin: theme.spacing(),
            fontSize: '13px',
            width: '100%',
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.buttonText.default
            },
        },
        "[class*='paletteButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.buttonText.default,
            margin: theme.spacing(),
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
        },
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    title: {
        marginLeft: 14,
        width: "900px",
        paddingTop: '4px'
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
        paddingTop: '4px'
    },
    customTooltip: {
        marginLeft: -16
    },
    tableTitleRefresh: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
});
let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const PrintQueueList = props => {
    const { classes, translations, saveColumnsFilter, setOpen, open, activeCustomer, tableColumns, onFetchPrintQueuesCount, printQueues, onFetchPrintQueues, tableRef } = props;
    const [dateRangeValue, setDateRangeValue] = useState(null);

    useEffect(() => {
        const customerTimezone = window.localStorage.getItem("CustomerTimezone") ?? 'UTC';

        const from = momentTimezone.tz(moment().subtract(2, 'days'), customerTimezone);

        const to = momentTimezone.tz(moment(), customerTimezone);
        
        setDateRangeValue({
            From: from,
            To: to,
            maxDate: to.set({
                hours: 23,
                minutes: 59,
                seconds: 59,
            }),
            query: {},
        });
        // eslint-disable-next-line
    }, [ activeCustomer ]);

    const customExport = (columns, printQueuesList) => {
        if (columns && columns[0].field === 'Action') {
            columns.shift()
        }
        const data = printQueuesList.map(printQueue => {
            let returnObj = [
                printQueue.LastModifiedAt ? formatDate(printQueue.LastModifiedAt) : translations['short_not_assigned'] || 'n/a',
                printQueue.Product && printQueue.Product.ColliInventoryID ? printQueue.Product.ColliInventoryID : translations['short_not_assigned'] || 'n/a',
                printQueue.Product.InventoryID ? printQueue.Product.InventoryID : translations['short_not_assigned'] || 'n/a',
                getProductData(printQueue, 'SerialNumber'),
                printQueue.Product && printQueue.Product.Type ? printQueue.Product.Type : translations['short_not_assigned'] || 'n/a',
                printQueue.Printer && printQueue.Printer.PrinterType ? printQueue.Printer.PrinterType : translations['short_not_assigned'] || 'n/a',
                printQueue.Printer && printQueue.Printer.ConnectionType ? printQueue.Printer.ConnectionType : translations['short_not_assigned'] || 'n/a',
                printQueue.Printer && printQueue.Printer.Name ? printQueue.Printer.Name : translations['short_not_assigned'] || 'n/a',
                printQueue.Printer ? printQueue.Printer.URI : translations['short_not_assigned'] || 'n/a',
                printQueue.OriginalLabel ? 'Original' : 'Copy',
                printQueue.Status === 1 ? "PENDING" : printQueue.Status === 0 ? 'PRINTED' : printQueue.Status === -2 ? 'FAILED' : 'PRINTING'
            ];
            return returnObj;
        });
        const builder = new CsvBuilder(`securaze_print_queue_${Math.floor(Date.now() / 1000)}.csv`);
        builder
            .setDelimeter('|')
            .setColumns(columns.map(columnDef => columnDef.title))
            .addRows(data)
            .exportFile();
    };

    const fetching = printQueues.fetching;
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []); const [tableOptions, setTableOptions] = useState({
            filtering: true,
            sorting: false,
            exportFileName: 'PrintQueue',
            filterCellStyle: { maxWidth: '200px' },
            search: false,
            minBodyHeight: window.innerHeight - 259 < 300 ? 300 : window.innerHeight - 259,
            maxBodyHeight: window.innerHeight - 259,
            pageSizeOptions: rowsPageSizeInit,
            exportButton: false,
            exportCsv: customExport,
            rowStyle: (_rowData, index) => {
                return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
            },
            columnsButton: true,
            pageSize: 50,
            emptyRowsWhenPaging: false
        });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['From'] = dateRangeValue?.From.format('YYYY-MM-DD');
        query['To'] = dateRangeValue?.To.format('YYYY-MM-DD');
        query['ActiveCustomerID'] = activeCustomer;
        query['q'] = q;
        let count = await onFetchPrintQueuesCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onFetchPrintQueues(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`PrintQueue`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer })
        return newColumnState
    };

    let formSettings = {
        Validation: {
            Name: Yup.string().required("Required"),
            Key: Yup.string().required("Required"),
        }
    };

    const filterDataRange = async (range) => {
        dateRangeValue.From = range.From
        dateRangeValue.To = range.To
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleCloseDanger = async (range) => {
        await props.onRePrint(open.printQueueID, { ActiveCustomerID: activeCustomer });
        setOpen({ open: false, printQueueID: null })
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleClose = async () => {
        setOpen({ open: false, printQueueID: null })
    };

    const tableTitle = () => {
        return <div className={classes.title}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography>
                    {translations["print_queues_last"] || 'Print Queues'}
                </Typography>

            </div>
            <div className={classes.tableTitleRefresh}>
                {dateRangeValue && <DataRange
                    formSettings={formSettings}
                    initialValues={dateRangeValue}
                    filterDataRange={filterDataRange}
                    value={dateRangeValue}
                    print={true}
                    setValue={setDateRangeValue}
                    format="yyyy/MM/dd 00:00:00"
                    translations={translations} />}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>

        </div>
    };

    return <>
        <MaterialTable
            tableRef={tableRef}
            title={tableTitle()}
            columns={columnObject}
            data={fetchTableData}
            onChangeRowsPerPage={(size) => {
                pageSize = size
            }}

            options={tableOptions}
            isLoading={fetching}
            localization={localization(translations)}
            onChangeColumnHidden={columnHidden}
            onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
        />
        <PrintQueue open={open} handleClose={handleClose} translations={translations} handleCloseDanger={handleCloseDanger} />
    </>
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchPrintQueuesCount: filter => dispatch(actions.fetchPrintQueueCount(filter)),
        onFetchPrintQueues: filter => dispatch(actions.fetchPrintQueue(filter)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onRePrint: (printQueueID, id) => dispatch(actions.rePrint(printQueueID, id))
    };
};

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        printQueues: state.printQueue.printQueues,
        permissions: state?.core?.login?.permissions || [],
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(PrintQueueList));